/**
 * Lifetime — everything a connection owns. One per connect(); dispose() releases it all.
 * @typedef {() => void} Cleanup
 */
export class Lifetime {
  #abort = new AbortController()
  /** @type {Set<Cleanup>} */
  #cleanups = new Set()

  /** Aborted on dispose. Pass it to fetch() and addEventListener(). */
  get signal() {
    return this.#abort.signal
  }

  get active() {
    return !this.#abort.signal.aborted
  }

  /** Number of registered cleanups (diagnostics and tests). */
  get size() {
    return this.#cleanups.size
  }

  /**
   * Register a cleanup. On a disposed lifetime it runs immediately, so late async
   * continuations cannot leak. Returns a function that forgets it without running it.
   * @param {Cleanup} cleanup
   * @returns {() => void}
   */
  add(cleanup) {
    if (!this.active) {
      run(cleanup)
      return () => {}
    }
    this.#cleanups.add(cleanup)
    return () => void this.#cleanups.delete(cleanup)
  }

  /**
   * addEventListener bound to this lifetime.
   * @param {EventTarget} target
   * @param {string} type
   * @param {EventListenerOrEventListenerObject} handler
   * @param {AddEventListenerOptions} [options]
   */
  listen(target, type, handler, options = {}) {
    if (!this.active) return
    target.addEventListener(type, handler, { ...options, signal: this.signal })
  }

  /**
   * setTimeout bound to this lifetime. Returns a cancel function.
   * @param {() => void} fn
   * @param {number} ms
   */
  timeout(fn, ms) {
    if (!this.active) return () => {}
    const id = setTimeout(() => {
      forget()
      fn()
    }, ms)
    const forget = this.add(() => clearTimeout(id))
    return () => {
      forget()
      clearTimeout(id)
    }
  }

  /**
   * setInterval bound to this lifetime. Returns a cancel function.
   * @param {() => void} fn
   * @param {number} ms
   */
  interval(fn, ms) {
    if (!this.active) return () => {}
    const id = setInterval(fn, ms)
    const forget = this.add(() => clearInterval(id))
    return () => {
      forget()
      clearInterval(id)
    }
  }

  /**
   * Start an observer (Mutation/Intersection/Resize) and disconnect it on dispose.
   * @template {{ observe: Function, disconnect: () => void }} T
   * @param {T} observer
   * @param {Node} target
   * @param {object} [options]
   * @returns {T}
   */
  observe(observer, target, options) {
    if (!this.active) return observer
    observer.observe(target, options)
    this.add(() => observer.disconnect())
    return observer
  }

  /** A nested lifetime: disposed with this one, or earlier on its own. */
  child() {
    const child = new Lifetime()
    child.add(this.add(() => child.dispose()))
    return child
  }

  /** Idempotent. Aborts the signal, then runs cleanups newest-first. */
  dispose() {
    if (!this.active) return
    this.#abort.abort()
    const cleanups = [...this.#cleanups].reverse()
    this.#cleanups.clear()
    for (const cleanup of cleanups) run(cleanup)
  }
}

/** @param {Cleanup} cleanup */
function run(cleanup) {
  try {
    cleanup()
  } catch (error) {
    console.error(error) // a broken cleanup must not block the remaining ones
  }
}
